import { useField } from 'formik';
import { ReasonableAdjustmentsOptionKind } from 'generated/graphql';
import { ChangeEvent, useCallback } from 'react';
import { getNotificationMessage } from 'views/Company/CompanyView/components/ReasonableAdjustmentsRadioButtonGroup/ReasonableAdjustmentsRadioButtonGroup.helpers';
import {
  FormikTextInputField,
  InlineNotification,
  NotificationType,
  RadioButton,
} from '@spotted-zebra-uk/ui-components';
import styles from './ReasonableAdjustmentsRadioButtonGroup.module.scss';

export const reasonableAdjustmentsFieldId = 'reasonableAdjustments';
export const reasonableAdjustmentsLabel =
  'Candidate reasonable adjustments experience';
const contactCompanyOptions = [
  ReasonableAdjustmentsOptionKind.ContactCompanyUrl,
  ReasonableAdjustmentsOptionKind.ContactCompanyEmail,
  ReasonableAdjustmentsOptionKind.ContactCompanyGeneric,
];

export default function ReasonableAdjustments({
  companyName,
  onChange,
  notificationVisibility,
}: {
  companyName: string;
  onChange: () => void;
  notificationVisibility: boolean;
}) {
  const [field, , helpers] = useField<ReasonableAdjustmentsOptionKind>(
    reasonableAdjustmentsFieldId
  );
  const [, contactCompanyWebsiteUrlFieldMeta] = useField<string>(
    'contactCompanyWebsiteUrl'
  );
  const [, contactCompanyEmailFieldMeta] = useField<string>(
    'contactCompanyEmail'
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      helpers.setValue(e.target.value as ReasonableAdjustmentsOptionKind);
      onChange();
    },
    [helpers, onChange]
  );

  const contactCompanySelected = contactCompanyOptions.includes(field.value);
  const notificationMessage = getNotificationMessage(companyName)[field.value];

  return (
    <div>
      <fieldset
        id={reasonableAdjustmentsFieldId}
        data-testid={`radio-button-group-${reasonableAdjustmentsFieldId}`}
        aria-label={reasonableAdjustmentsLabel}
        className={styles.radioButtonGroup}
      >
        <legend
          data-testid="radio-button-group-legend"
          className={styles.title}
        >
          {reasonableAdjustmentsLabel}
        </legend>
        <div className={styles.items}>
          <RadioButton
            key="Self-serve"
            name="Self-serve"
            id="selfServe"
            value={ReasonableAdjustmentsOptionKind.SelfServe}
            checked={field.value === ReasonableAdjustmentsOptionKind.SelfServe}
            ariaLabel="Self-serve"
            onChange={handleChange}
          >
            Self-serve
          </RadioButton>
          <RadioButton
            key="Contact company"
            name="Contact company"
            id="contactCompany"
            value={ReasonableAdjustmentsOptionKind.ContactCompanyUrl}
            checked={contactCompanySelected}
            ariaLabel="Contact company"
            onChange={handleChange}
          >
            Contact company
          </RadioButton>
          {contactCompanySelected && (
            <div className={styles.nestedFields}>
              <RadioButton
                key="Contact company via website URL"
                name="Contact company via website URL"
                id="contactCompanyUrl"
                value={ReasonableAdjustmentsOptionKind.ContactCompanyUrl}
                checked={
                  field.value ===
                  ReasonableAdjustmentsOptionKind.ContactCompanyUrl
                }
                ariaLabel="Contact company via website URL"
                onChange={handleChange}
              >
                <FormikTextInputField
                  id="contactCompanyWebsiteUrl"
                  label="Website URL"
                  placeholder="Website URL"
                  useFormikField={useField}
                  bottomText={contactCompanyWebsiteUrlFieldMeta.error}
                />
              </RadioButton>
              <RadioButton
                key="Contact company via email"
                name="Contact company via email"
                id="contactCompanyEmail"
                value={ReasonableAdjustmentsOptionKind.ContactCompanyEmail}
                checked={
                  field.value ===
                  ReasonableAdjustmentsOptionKind.ContactCompanyEmail
                }
                ariaLabel="Contact company via email"
                onChange={handleChange}
              >
                <FormikTextInputField
                  id="contactCompanyEmail"
                  label="Email"
                  placeholder="Email"
                  useFormikField={useField}
                  bottomText={contactCompanyEmailFieldMeta.error}
                />
              </RadioButton>
              <RadioButton
                key="Contact company via indirect contact"
                name="Contact company via indirect contact"
                id="contactCompanyIndirectContact"
                value={ReasonableAdjustmentsOptionKind.ContactCompanyGeneric}
                checked={
                  field.value ===
                  ReasonableAdjustmentsOptionKind.ContactCompanyGeneric
                }
                ariaLabel="Contact company via indirect contact"
                onChange={handleChange}
              >
                Indirect contact
              </RadioButton>
            </div>
          )}
          <RadioButton
            key="Email Spotted Zebra"
            name="Email Spotted Zebra"
            id="emailSz"
            value={ReasonableAdjustmentsOptionKind.EmailSz}
            checked={field.value === ReasonableAdjustmentsOptionKind.EmailSz}
            ariaLabel="Email Spotted Zebra"
            onChange={handleChange}
          >
            Email Spotted Zebra
          </RadioButton>
        </div>
      </fieldset>
      {notificationVisibility && (
        <InlineNotification
          notificationType={NotificationType.NEUTRAL}
          className={styles.inlineNotification}
        >
          {notificationMessage}
        </InlineNotification>
      )}
    </div>
  );
}
